import * as React from "react";
import { styled, useTheme } from "@mui/material/styles";
import MuiAccordion, { AccordionProps } from "@mui/material/Accordion";
import MuiAccordionSummary, { AccordionSummaryProps } from "@mui/material/AccordionSummary";
import MuiAccordionDetails, { AccordionDetailsProps } from "@mui/material/AccordionDetails";
import { Attention } from "./Attention";
import { Text, TextProps } from "./Typography";
import { Box, Grid, SxProps, Theme, Typography, useMediaQuery } from "@mui/material";
import { sizing } from "../utils/methods";
import { Blog } from "../pages/About/blogs";
import { AddRounded, ExpandMoreRounded, RemoveRounded } from "@mui/icons-material";
// import { useNavigate } from "react-router";
import { AppRoutes } from "../config";
import { MOBILE_FRAME_PX, MOBILE_MAX_SCALE, u } from "./HomeHero/heroScale";

export const Accordion = styled((props: AccordionProps) => (
  <MuiAccordion disableGutters elevation={0} square {...props} />
))(() => ({
  borderBottom: `var(--border)`,
  border: "none",
  background: "none",
  color: "black",
  "&:not(:last-child)": {
    borderBottom: 0,
  },
  "&::before": {
    display: "none",
  },
}));

const AccordionSummary = styled(MuiAccordionSummary)`
  background: none;
  border: none;
  padding: 0;
  flex-direction: row;

  & .MuiAccordionSummary-content {
    // margin: 24px 0;

    @media (max-width: 992px) {
      margin: 12px 0;
    }
  }
`;

const StyledSummary = (props: AccordionSummaryProps & { info?: boolean }) => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const icon = isMobile ? (
    props["aria-expanded"] ? (
      <RemoveRounded sx={{ color: "black" }} />
    ) : (
      <AddRounded sx={{ color: "common.black" }} />
    )
  ) : (
    <ExpandMoreRounded sx={{ color: "common.black" }} />
  );

  const newProps = {
    ...props,
    info: undefined,
    expandIcon: props.info ? undefined : icon,
  };

  return <AccordionSummary {...newProps} />;
};

export const AccordionDetails = (props: AccordionDetailsProps) => (
  <MuiAccordionDetails {...props} sx={{ ...props.sx, padding: sizing(0) }} />
);

type BlockProps = {
  blog: Blog;
  search?: string;
  info?: boolean;
  sx?: SxProps<Theme>;
  subtitle?: string;
  paragraph?: string;
  explore?: string;
  /** Below md, size the spacing in HomeHero's units (heroScale) instead of fixed px, so the banner
   *  grows with its width like the hero beside it; the type and button keep their design size.
   *  Opt-in: only the homepage banners sit next to the hero. */
  fluid?: boolean;
};

/** Side gutters of a fluid BlogStoryBlock (fixed px, like HomeHero's copy card). */
const FLUID_GUTTER_PX = 18;
const FLUID_GUTTER_DESKTOP_PX = 24;
/** HomeHero's `--u` (heroScale.MOBILE_UNIT_VAR), measured on the whole banner: the root is the
 *  container and `cqw` is its content box, so add the gutters back to get the banner's width. */
const FLUID_UNIT_VAR = `clamp(1px, calc((100cqw + ${2 * FLUID_GUTTER_PX}px) / ${MOBILE_FRAME_PX}), ${MOBILE_MAX_SCALE}px)`;

export const BlogStoryBlock = ({ blog, info, search, sx, subtitle, paragraph, explore, fluid }: BlockProps) => {
  // const navigate = useNavigate();
  const [expanded, setExpanded] = React.useState(false);
  // Mobile spacing: a hero unit when fluid, else the fixed px it always was.
  const xs = (px: number) => (fluid ? u(px) : `${px}px`);

  // Use subtitle and paragraph if provided, otherwise fallback to blog properties
  const displayTitle = subtitle || blog.title;
  const displayDescription = paragraph || blog.description;

  return (
    <Grid
      width="100%"
      flexDirection="column"
      display={"flex"}
      justifyContent={"space-between"}
      sx={{
        paddingX: sizing(0),
        background: 'transparent',
        color: 'black',
        margin: "auto",
        marginLeft: { xs: "20px", md: "120px" },
        // Full width with the gutter as padding (the margin pushed the text 20px past the
        // banner's right edge), and `--u` set for the children below md.
        ...(fluid && {
          boxSizing: "border-box",
          marginLeft: 0,
          paddingX: { xs: `${FLUID_GUTTER_PX}px`, md: `${FLUID_GUTTER_DESKTOP_PX}px` },
          // Breathing room above the title and below the button; the banner grows to fit
          // (EcoBank's Trending is min-height below md, not a fixed height).
          paddingY: { xs: u(32), md: 0 },
          containerType: { xs: "inline-size", md: "normal" },
          "--u": { xs: FLUID_UNIT_VAR },
        }),
        ...sx
      }}
    >
      <Attention inverted sx={{
        justifyContent: "space-between",
        display: "flex",
        alignItems: "baseline",
        border: "none",
        paddingY: sizing(0),
        mb: { xs: xs(fluid ? 12 : 10), md: "40px" }
      }}>
        <Text capital={false}
          sx={{ font: "inherit", fontSize: { xs: "18px", md: "26px" }, fontWeight: "600", color: "white", width: { xs: "100%", md: "50%" } }} >
          <div id="blogTitle">
            {displayTitle}
          </div>
        </Text>


      </Attention>
      <Text
        sx={{
          flexDirection: "column",
          textDecoration: info ? "none" : "none",
          color: "white",
          mb: { xs: xs(fluid ? 24 : 10), md: "40px" },
          width: { xs: "100%", md: "50%" },
          fontSize: { xs: "14px", md: "16px" },
        }}
        dangerouslySetInnerHTML={{ __html: displayDescription }}
      />
      {/* Plain flex row, not an Accordion: this block has no AccordionDetails and
          never expands (info renders no expand icon), so the Accordion was pure
          layout — and its AccordionSummary renders a ButtonBase, which put the
          AnimatedButton below inside a <button>. Nested buttons are invalid HTML
          and leave keyboard/AT behaviour ambiguous. */}
      <Box
        sx={{
          display: "flex",
          alignItems: "baseline",
          // Fluid spaces the button with the paragraph's margin and the root's padding instead.
          "@media (max-width: 992px)": { margin: fluid ? 0 : "12px 0" },
        }}
      >
          <AnimatedButton
            onClick={() => {
              if (!info) {
                let path = AppRoutes.About.path + "?blog=";
                if (explore) {
                  path = explore;
                }
                else if (search) {
                  path += search;
                } else if (blog.id) {
                  if (search) {
                    path += "blog-article-" + blog.id + "&search=" + search;
                  } else {
                    path += "blog-article-" + blog.id + "&search=" + blog.title;
                  }
                } else if (blog.title) {
                  path += blog.title;
                } else {
                  setExpanded(!expanded);
                }
                if (blog.openInNew) {
                  window.open(path, "_blank");
                } else {
                  // navigate(path);
                  window.open(path, "_blank");
                }
              }
            }}
            sx={{ fontSize: { xs: "14px", md: "16px" } }}
          >
            {blog.buttonText}
          </AnimatedButton>
      </Box>
    </Grid>
  );
};

type CollapseBlockProps = {
  title: string;
  content: any;
};
export const CollapseBlock = ({ title, content }: CollapseBlockProps) => {
  const [expanded, setExpanded] = React.useState(false);
  return (
    <Grid width="100%" flexDirection="column">
      <Accordion expanded={expanded} onChange={() => setExpanded(!expanded)}>
        <StyledSummary
          aria-controls="panel1d-content"
          id="panel1d-header"
          sx={{ borderBottom: "1px solid black", paddingY: sizing(6) }}
        >
          <Text medium>{title}</Text>
        </StyledSummary>
        <AccordionDetails>
          <Text medium sx={{ display: "block" }}>
            {content}
          </Text>
        </AccordionDetails>
      </Accordion>
    </Grid>
  );
};

export default function CustomizedAccordions() {
  const [expanded, setExpanded] = React.useState<string | false>(false);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, newExpanded: boolean) => {
    setExpanded(newExpanded ? panel : false);
  };

  return (
    <Wrap sx={{ color: "black", paddingBottom: "24px", }}>
      <Attention inverted sx={{ border: 0 }}>
        <Text large bold>
          Frequently asked questions
        </Text>
      </Attention>

      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        Portfolio
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel1"} onChange={handleChange("panel1")}>
        <StyledSummary aria-expanded={expanded === "panel1"} aria-controls="panel1d-content" id="panel1d-header">
          <QuestionTitle>How is a land's ecology assessed?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            An EcoFutures Trustee will evaluate a land based on the Eco Matrix,—an evaluation system that ensures a
            fair, accurate appraisal of land's ecological value to benefit both investors and land owners developed
            using 40 years of research by Analog Forestry founder Dr. Ranil. The Eco matrix factors soil health, water
            quality, carbon sequestration, forest maturity and cooling effects, flora and fauna biodiversity, energy use
            and employment benefits to local comminities in order to generate a 0 to 100 eco coefficient. The Eco Matrix
            will evolve as technology advances to better measure the value of a perfect forest.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Portfolio</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        Portfolio
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel2"} onChange={handleChange("panel2")}>
        <StyledSummary aria-expanded={expanded === "panel2"} aria-controls="panel2d-content" id="panel2d-header">
          <QuestionTitle>Who are the EcoFutures Trustees?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            EcoFutures Trustees are qualified, carefully selected professionals and organizations that guarantee the
            integrity of evaluating and verifying the lands listed on the platform. Most often, EcoFutures Trustees are
            permaculture experts, ecologists, and experienced members of registered environmental conservation
            organizations that have been in operation for over 5 years, such as the Analog Forestry Network
            International, Rainforest Protectors Sri Lanka, and UNDP REDD+. They act as real-world oracles ensuring
            third party integrity of all lands on the protocol.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Portfolio</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        Portfolio
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel3"} onChange={handleChange("panel3")}>
        <StyledSummary aria-expanded={expanded === "panel3"} aria-controls="panel3d-content" id="panel3d-header">
          <QuestionTitle>How are lands on the protocol verified?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            The verification process of each land listed on the platform includes a visit to the land by an EcoFutures
            Trustee to ascertain its existence as well as the accuracy of the documents and data submitted. Once a
            Trustee confirms a lands ownership and attributes, they inspect the lands ecological health that defines the
            lands currency generating potential. A land that has successfully auctioned will be re-assessed at regular
            intervals by an EcoFutures Trustee in exchange for 7%-10% of the lands auction value, ensuring that
            conditions specified in the ecosystem services contract are upheld and the lands ecology is preserved over
            the contract length.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Portfolio</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        Terminal
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel4"} onChange={handleChange("panel4")}>
        <StyledSummary aria-expanded={expanded === "panel4"} aria-controls="panel4d-content" id="panel4d-header">
          <QuestionTitle>How are TREEs minted?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            A TREE is minted for every 100 square meters of land that's sustained for a year, depending on the land's
            ecological coefficient. This links each TREE directly to the land's ecological value, showcasing its
            positive impact on local communities and the environment.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Terminal</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        TREEs
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel5"} onChange={handleChange("panel5")}>
        <StyledSummary aria-expanded={expanded === "panel5"} aria-controls="panel5d-content" id="panel5d-header">
          <QuestionTitle>How are funds distributed?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            97% of all funds go directly to landowners (EcoFutures Guardians) and validators (EcoFutures Trustees),
            ensuring maximum support for all ecological ventures. 3% is allocated to the EcoFutures foundation to
            maintain server and transaction fees and give back to the future.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Terminal</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        TREEs
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel6"} onChange={handleChange("panel6")}>
        <StyledSummary aria-expanded={expanded === "panel6"} aria-controls="panel6d-content" id="panel6d-header">
          <QuestionTitle>How is a halving indued on the EcoFutures protocol?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            Fibonacci halving is apparent throughout nature—from the petals of a sunflower and a snail's shell to the
            branches and roots of a banyan tree. EcoFutures follows the same halving sequence of
            1,1,2,3,5,8,13,21,34,55,89... Early platform growth generates the highest TREE mint rates. As platform
            coverage advances through Fibonacci-based edition thresholds, the mint rate reduces by the inverse Fibonacci
            sequence, making Tree (EFT) a deflationary ESG Asset Class
            backed by real forests.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>TREEs</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        Portfolio
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel7"} onChange={handleChange("panel7")}>
        <StyledSummary aria-expanded={expanded === "panel7"} aria-controls="panel7d-content" id="panel7d-header">
          <QuestionTitle>What happens if a land's ecology gets compromised?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            If a land's ecology gets compromised—whether due to a breach of the ecosystem services agreement, or
            eco-destruction events like forest fires, landslides, or flash floods—the trust oracle may block the land
            from generating TREEs and stop all future funding to the guardian. In such cases, all remaining funds are
            returned to the investor, and the land is removed from the protocol as a TREE minting asset.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>Portfolio</Attention> */}


      <Typography
        variant="body1"
        sx={{
          fontSize: "16px",
          fontFamily: "Inter, Helvetica",
          fontWeight: "normal",
          paddingBottom: "16px",
        }}
      >
        TREEs
      </Typography>
      <Accordion sx={{ borderTop: "1px solid black", paddingBottom: "25px" }} expanded={expanded === "panel8"} onChange={handleChange("panel8")}>
        <StyledSummary aria-expanded={expanded === "panel8"} aria-controls="panel8d-content" id="panel8d-header">
          <QuestionTitle>How can I turn my money into green currency TREEs (EFT)?</QuestionTitle>
        </StyledSummary>
        <AccordionDetails>
          <Text large>
            You can easily buy TREEs using your Metamask wallet for a quick and secure transaction. If you prefer paying
            with a card, Moonpay's trusted platform has you covered, allowing you to turn your money into TREEs
            effortlessly.
          </Text>
        </AccordionDetails>
      </Accordion>
      {/* <Attention inverted>TREEs</Attention> */}

    </Wrap>
  );
}

const QuestionTitle = (props: TextProps) => {
  return <Text large underline sx={{ fontWeight: "400 !important" }} {...props} />;
};

const Wrap = styled(Grid)`
  & > div:last-of-type.Mui-expanded {
    margin-bottom: 24px;

    @media (max-width: 992px) {
      margin-bottom: 12px;
    }
  }

  & > div:nth-of-type(odd) {
    border-bottom-color: ${({ theme }) => theme.palette.border.dark};
  }
`;

const AnimatedButton = styled('button')`
  font-family: Inter, sans-serif;
  color: white;
  background-color: transparent;
  padding: 15px 25px;
  border-radius: 15px;
  width: fit-content;
  border: 1px solid white;
  cursor: pointer;
 

  &:hover {
    background-color: white;
    color: black;
    transform: translateY(-2px);
    box-shadow: 0 5px 15px rgba(0, 0, 0, 0.2);
  }

  // &:active {
  //   transform: translateY(0);
  //   box-shadow: 0 2px 5px rgba(0, 0, 0, 0.2);
  // }
`;
